import { Link, NavLink } from "react-router-dom";
import { useState } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSiteSettings } from "@/lib/settings";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/jobs", label: "Openings" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const { settings } = useSiteSettings();

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/85 backdrop-blur">
      <div className="container-page flex h-20 items-center justify-between md:h-24">
        <Link to="/" className="flex items-center gap-3 group" data-testid="site-logo-link">
          <span className="logo-mark inline-flex h-14 w-14 items-center justify-center transition-transform duration-300 group-hover:scale-105 md:h-16 md:w-16">
            <img
              src={settings.logo_url}
              alt={`${settings.brand_left}${settings.brand_right} logo`}
              className="h-full w-full rounded-md object-cover"
            />
          </span>
          <div className="hidden flex-col leading-tight sm:flex">
            <span className="font-heading text-xl font-bold tracking-tight text-slate-900 md:text-2xl">
              <span className="text-primary">{settings.brand_left}</span>
              <span className="text-secondary">{settings.brand_right}</span>
            </span>
            <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">{settings.tagline}</span>
          </div>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" data-testid="site-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${isActive ? "text-primary" : "text-slate-600 hover:text-slate-900"}`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <Link to="/upload-resume" data-testid="header-upload-resume">
            <Button className="bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:bg-primary/90">{(settings?.upload_resume_button_text || "").trim() || "Upload Resume"}</Button>
          </Link>
          <Link to="/hire-talent" data-testid="header-hire-talent">
            <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90">{settings?.hire_talent_button_text || "Hire Talent"}</Button>
          </Link>
        </div>

        <button
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="rounded-md p-2 text-slate-700 hover:bg-slate-100 md:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white md:hidden" data-testid="mobile-menu">
          <div className="container-page flex flex-col gap-1 py-3">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `rounded-md px-3 py-2 text-base font-medium ${isActive ? "bg-primary/10 text-primary" : "text-slate-700 hover:bg-slate-50"}`
                }
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              >
                {item.label}
              </NavLink>
            ))}
            <div className="mt-3 flex flex-col gap-2 pt-3">
              <Link to="/upload-resume" onClick={() => setOpen(false)}>
                <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90">{(settings?.upload_resume_button_text || "").trim() || "Upload Resume"}</Button>
              </Link>
              <Link to="/hire-talent" onClick={() => setOpen(false)}>
                <Button className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90">{settings?.hire_talent_button_text || "Hire Talent"}</Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
